import { Box } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRevealWage } from "../../../hooks/useRevealWage";
import { PushReportType, StatsMode } from "../../../utils/models";
import { useGetManagementCenterHppdStats, useGetManagementCenterPpdStats, useManagementCenterFilters } from "../hooks";
import { ManagementCenterHeader } from "../ManagementCenterHeader";
import { MaximizeButton } from "../MaximizeButton";
import { MaximizedChartDialog } from "../MaximizedChartDialog";
import { useDownloadScheduleVsActual } from "../useDownloadReport";
import { buildClampRange, MC_PAGE_BG } from "../utils";
import { ScheduleVsActualChart } from "./ScheduleVsActualChart";
import { ScheduleVsActualHeader } from "./ScheduleVsActualHeader";
import { ScheduleVsActualTable } from "./ScheduleVsActualTable";
import type { DifferenceMode, Metric } from "./types";

export const ScheduleVsActualReport: FC = () => {
	const canViewWage = useRevealWage();
	const { track } = useAnalytics();
	const { chosenDepartment, timezone } = useHospitalData();
	const isRatioMode = chosenDepartment.statsMode === StatsMode.Ratio;
	const {
		shiftHourOptions,
		shiftHoursUpdating,
		shiftHoursUnavailable,
		mcSettingsReady,
		sinceDate,
		endDate,
		onStartDateChange,
		onEndDateChange,
		selectedRoles,
		onRolesChange,
		appliedRoleFilter,
		selectedJobs,
		onJobsChange,
		appliedJobFilter,
		selectedShiftHours,
		onShiftHoursChange,
		shiftHoursForApi,
		laborCostEnabled,
		onLaborCostToggle,
		includeHolidays,
		onIncludeHolidaysToggle,
		excludeHolidaysForApi,
		includeBreaksForApi,
		includeLateScheduled,
		onIncludeLateScheduledToggle,
		includeLateScheduledForApi,
		estimateMissingWage,
		onEstimateMissingWageToggle,
		onEstimateMissingWageNoticeDismissed,
		wantedMode,
		onWantedModeToggle,
		wantedModeForApi,
		otMode,
		onOtModeChange,
		onAlignToDayView,
		onSaveMcDefaults,
		onRestoreMcSettings,
		searchParams,
		setSearchParams,
	} = useManagementCenterFilters({ trackPrefix: "schedule_vs_actual" });

	const clampRange = useMemo(() => buildClampRange(sinceDate, endDate, timezone), [sinceDate, endDate, timezone]);
	const rawMetric = (searchParams.get("metric") as Metric) || "hppd";
	const metric = !canViewWage ? "hppd" : rawMetric;
	const isPpd = metric === "ppd";

	const differenceMode = (searchParams.get("diffMode") as DifferenceMode) || "target";
	const handleDifferenceModeChange = (mode: DifferenceMode) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("diffMode", mode);
			return next;
		});
		track("schedule_vs_actual_toggle_diff_mode", { mode });
	};

	const { data: ppdDataGrouped, isLoading: isLoadingPpdGrouped } = useGetManagementCenterPpdStats({
		sinceDate,
		endDate,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		enabled: isPpd && mcSettingsReady,
		laborCost: laborCostEnabled,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		wantedMode: wantedModeForApi,
		hideWage: !canViewWage,
		otMode,
		estimateMissingWage,
	});
	const { data: hppdDataGrouped, isLoading: isLoadingHppdGrouped } = useGetManagementCenterHppdStats({
		sinceDate,
		endDate,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		enabled: !isPpd && mcSettingsReady,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		wantedMode: wantedModeForApi,
		otMode,
	});
	const { data: ppdDataDaily, isLoading: isLoadingPpdDaily } = useGetManagementCenterPpdStats({
		sinceDate,
		endDate,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		grouping: false,
		enabled: isPpd && mcSettingsReady,
		laborCost: laborCostEnabled,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		wantedMode: wantedModeForApi,
		hideWage: !canViewWage,
		otMode,
		estimateMissingWage,
	});
	const { data: hppdDataDaily, isLoading: isLoadingHppdDaily } = useGetManagementCenterHppdStats({
		sinceDate,
		endDate,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		grouping: false,
		enabled: !isPpd && mcSettingsReady,
		shiftHours: shiftHoursForApi,
		excludeHolidays: excludeHolidaysForApi,
		includeLateScheduled: includeLateScheduledForApi,
		wantedMode: wantedModeForApi,
		otMode,
	});

	const chartData = isPpd ? ppdDataGrouped : hppdDataGrouped;
	// `|| !mcSettingsReady`: these queries are parameterized by persisted `mc_*` settings and stay gated
	// off until those resolve; a gated-off query reports `isLoading: false`, so without this the page
	// would render an empty chart/table for the settings round-trip instead of its skeleton.
	const chartLoading = (isPpd ? isLoadingPpdGrouped : isLoadingHppdGrouped) || !mcSettingsReady;
	const tableData = isPpd ? ppdDataDaily : hppdDataDaily;
	const tableLoading = (isPpd ? isLoadingPpdDaily : isLoadingHppdDaily) || !mcSettingsReady;

	const handleMetricToggle = (newMetric: Metric) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("metric", newMetric);
			return next;
		});
		track("schedule_vs_actual_toggle_metric", { metric: newMetric });
	};

	const hasTrackedViewed = useRef(false);
	useEffect(() => {
		if (!hasTrackedViewed.current) {
			track("schedule_vs_actual_report_viewed");
			hasTrackedViewed.current = true;
		}
	}, [track]);

	const { mutateAsync: downloadScheduleVsActual, isPending: isDownloading } = useDownloadScheduleVsActual();
	const onDownload = useCallback(
		() =>
			downloadScheduleVsActual({
				startDate: sinceDate,
				endDate,
				metric,
				shiftHours: shiftHoursForApi,
				roles: appliedRoleFilter,
				jobs: appliedJobFilter,
				laborCost: laborCostEnabled,
				excludeHolidays: excludeHolidaysForApi,
				includeBreaks: includeBreaksForApi,
				includeLateScheduled: includeLateScheduledForApi,
				hideWage: !canViewWage,
				wantedMode: wantedModeForApi,
				diffMode: differenceMode,
				otMode,
				estimateMissingWage,
			}),
		[
			downloadScheduleVsActual,
			sinceDate,
			endDate,
			metric,
			shiftHoursForApi,
			appliedRoleFilter,
			appliedJobFilter,
			laborCostEnabled,
			excludeHolidaysForApi,
			includeBreaksForApi,
			includeLateScheduledForApi,
			canViewWage,
			wantedModeForApi,
			differenceMode,
			otMode,
			estimateMissingWage,
		],
	);

	const [chartMaximized, setChartMaximized] = useState(false);
	const handleMaximize = useCallback(() => {
		setChartMaximized(true);
		track("schedule_vs_actual_chart_maximize_opened");
	}, [track]);
	const handleCloseMaximize = useCallback(() => {
		setChartMaximized(false);
		track("schedule_vs_actual_chart_maximize_closed");
	}, [track]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				bgcolor: MC_PAGE_BG,
				overflow: "auto",
				padding: 2,
				overflowX: "hidden",
			}}
		>
			<ScheduleVsActualHeader />
			<ManagementCenterHeader
				onStartDateChange={onStartDateChange}
				onEndDateChange={onEndDateChange}
				onRolesChange={onRolesChange}
				selectedRoles={selectedRoles}
				selectedJobs={selectedJobs}
				onJobsChange={onJobsChange}
				showActualToggle={false}
				metric={metric}
				onMetricToggle={handleMetricToggle}
				shiftHourOptions={shiftHourOptions}
				shiftHoursUpdating={shiftHoursUpdating}
				shiftHoursUnavailable={shiftHoursUnavailable}
				selectedShiftHours={selectedShiftHours}
				onShiftHoursChange={onShiftHoursChange}
				onDownload={onDownload}
				isDownloading={isDownloading}
				pushReportType={PushReportType.schedule_vs_actual}
				laborCostEnabled={laborCostEnabled}
				onLaborCostToggle={onLaborCostToggle}
				includeHolidays={includeHolidays}
				onIncludeHolidaysToggle={onIncludeHolidaysToggle}
				includeLateScheduled={includeLateScheduled}
				onIncludeLateScheduledToggle={onIncludeLateScheduledToggle}
				estimateMissingWage={estimateMissingWage}
				onEstimateMissingWageToggle={onEstimateMissingWageToggle}
				onEstimateMissingWageNoticeDismissed={onEstimateMissingWageNoticeDismissed}
				wantedMode={isRatioMode ? wantedMode : undefined}
				onWantedModeToggle={isRatioMode ? onWantedModeToggle : undefined}
				otMode={otMode}
				onOtModeChange={onOtModeChange}
				onAlignToDayView={onAlignToDayView}
				onSaveMcDefaults={onSaveMcDefaults}
				onRestoreMcSettings={onRestoreMcSettings}
			/>
			<Box
				sx={{
					display: "flex",
					gap: 2,
					mt: 2,
					alignItems: "flex-start",
				}}
			>
				<ScheduleVsActualChart
					data={chartData}
					metric={metric}
					isLoading={chartLoading}
					trailing={<MaximizeButton onClick={handleMaximize} />}
					clampRange={clampRange}
				/>
				<ScheduleVsActualTable
					data={tableData}
					metric={metric}
					isLoading={tableLoading}
					roles={appliedRoleFilter}
					jobs={appliedJobFilter}
					shiftHours={shiftHoursForApi}
					laborCost={laborCostEnabled}
					differenceMode={differenceMode}
					onDifferenceModeChange={handleDifferenceModeChange}
					includeLateScheduled={includeLateScheduledForApi}
					estimateMissingWage={estimateMissingWage}
				/>
			</Box>
			<MaximizedChartDialog open={chartMaximized} onClose={handleCloseMaximize}>
				<ScheduleVsActualChart
					data={chartData}
					metric={metric}
					isLoading={chartLoading}
					containerSx={{ width: "100%", height: "100%" }}
					clampRange={clampRange}
				/>
			</MaximizedChartDialog>
		</Box>
	);
};
